import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationRepeat, NotificationTrigger } from '../../models/enums/notifications';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import { useSearchParams } from 'react-router-dom';
import { FilterKeys } from '@common/enums/query-params/filters';
import { convertReportParametersToQuery } from '../../models/general/notifications/report-parameters';
import { DateTime } from 'luxon';
import DateInput from '@common/components/Inputs/DateInput';

const MissionReportDurationPopover: React.FC<{
  anchorEl: HTMLButtonElement | null;
  organizationId?: string;
  robotId?: string;
  onClose: () => void;
}> = ({ anchorEl, organizationId, robotId, onClose }) => {
  const { t } = useTranslation();

  const [, setFilterParams] = useSearchParams();

  const [isCustomRangeOpen, setIsCustomRangeOpen] = useState(false);
  const [customStart, setCustomStart] = useState<DateTime | null>(null);
  const [customEnd, setCustomEnd] = useState<DateTime | null>(null);

  const openReport = (startTime: DateTime, endTime: DateTime) => {
    setFilterParams((prevState) => {
      prevState.set(
        FilterKeys.Report,
        convertReportParametersToQuery({
          trigger: NotificationTrigger.MISSION_SUMMARY,
          organizationId,
          robotId,
          startTime: startTime.toISO()!,
          endTime: endTime.toISO()!,
        }),
      );

      return prevState;
    });
    closeHandler();
  };

  const closeHandler = () => {
    setIsCustomRangeOpen(false);
    setCustomStart(null);
    setCustomEnd(null);
    onClose();
  };

  const selectDurationClickHandler = (duration: NotificationRepeat) => {
    let startTime = DateTime.now().startOf('day');
    const endTime = DateTime.now();
    switch (duration) {
      case NotificationRepeat.END_OF_DAY:
        break;
      case NotificationRepeat.END_OF_WEEK:
        startTime = startTime.minus({ days: 7 });
        break;
      case NotificationRepeat.END_OF_MONTH:
        startTime = startTime.minus({ months: 1 });
        break;

      default:
        break;
    }
    openReport(startTime, endTime);
  };

  const now = DateTime.now();
  const isCustomRangeValid =
    customStart != null &&
    customEnd != null &&
    customStart.isValid &&
    customEnd.isValid &&
    customStart.startOf('day') <= customEnd.startOf('day');

  const customRangeDownloadClickHandler = () => {
    if (!isCustomRangeValid) return;
    const endOfSelectedDay = customEnd!.endOf('day');
    openReport(customStart!.startOf('day'), endOfSelectedDay > now ? now : endOfSelectedDay);
  };

  return (
    <React.Fragment>
      <Popover
        id="mission-report-duration-popover-id"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeHandler}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Stack>
          {Object.values(NotificationRepeat).map((repeat) => (
            <Button
              sx={(theme) => ({
                px: 2,
                justifyContent: 'space-between',
                width: theme.spacing(34),
                color: theme.palette.action.active,
              })}
              onClick={() => selectDurationClickHandler(repeat)}
              key={repeat}
              size="large"
            >
              {t('report-repeat-' + repeat)}
            </Button>
          ))}
          <Button
            sx={(theme) => ({
              px: 2,
              justifyContent: 'space-between',
              width: theme.spacing(34),
              color: isCustomRangeOpen ? undefined : theme.palette.action.active,
            })}
            onClick={() => setIsCustomRangeOpen((prevState) => !prevState)}
            data-testid="report-custom-range-button"
            size="large"
          >
            {t('report-duration-custom')}
          </Button>
          {isCustomRangeOpen && (
            <React.Fragment>
              <Divider />
              <Stack spacing={1} px={2} pb={2}>
                <DateInput
                  date={customStart}
                  title={t('input-label-start-date')}
                  maxDate={now}
                  onDateTimeChange={setCustomStart}
                  inputTestId="report-custom-range-start-date"
                />
                <DateInput
                  date={customEnd}
                  title={t('input-label-end-date')}
                  minDate={customStart ?? undefined}
                  maxDate={now}
                  onDateTimeChange={setCustomEnd}
                  inputTestId="report-custom-range-end-date"
                />
                <Button
                  variant="contained"
                  disabled={!isCustomRangeValid}
                  onClick={customRangeDownloadClickHandler}
                  data-testid="report-custom-range-download-button"
                >
                  {t('button-download')}
                </Button>
              </Stack>
            </React.Fragment>
          )}
        </Stack>
      </Popover>
    </React.Fragment>
  );
};

export default MissionReportDurationPopover;
